(function () {
  if (window.customElements && customElements.get('side-cart-drawer')) return;

  var CART_PATHS = /\/cart\/(add|change|update|clear)(\.js|\.json)?$/;
  var ADD_PATH = /\/cart\/add(\.js|\.json)?$/;
  var GIFT_APP_PARAM = '_easygift_internal';
  var CART_APP_SCRIPT = 'script[src*="cartdrawer.nyc3"], script[data-src*="cartdrawer.nyc3"]';
  var LOCK_PROPS = ['position', 'top', 'overflow', 'width'];
  var LOADING_TIMEOUT = 5000;
  var SLIDE_GAP = 50;
  // Peeking tiles touch, and the -1px lets neighbouring borders share one line.
  var PEEK_GAP = -1;
  var SLIDE_DURATION = 300;
  var PEEK_RESERVE = 72;
  var PEEK_MIN_RATIO = 0.6;
  // A horizontal drag this far moves one offer. Shorter drags spring back.
  var SWIPE_THRESHOLD = 40;
  // Movement before a drag counts as a swipe or a page scroll, not a tap.
  var SWIPE_SLOP = 8;
  var SWIPE_CLICK_WINDOW = 400;
  var UPLOAD_PARAM = 'open_upload';
  var UPLOAD_BUTTON = '#dot-painting-app-root .upload-trigger-btn';
  var UPLOAD_WAIT = 15000;
  var FRAME_PROPERTIES = ['_Frame', '_dot_painting_frame_id', '_dot_painting_frame_name'];
  // A page that comes back into focus re-reads the cart (another tab may have
  // changed it), at most this often.
  var FOCUS_REFRESH_GAP = 2000;
  // skipNextAutoOpen(): how long the skip waits for an add, and how long it
  // stays on after the first skipped add (one add can be several requests).
  var SKIP_OPEN_WAIT = 30000;
  var SKIP_OPEN_TAIL = 2000;
  // A refresh draws again at most this often when the cart moved on while it
  // was drawing, before it sends the cart it has.
  var REDRAW_LIMIT = 2;
  // If the cart is still moving after those redraws, wait this long and
  // try one more round. Then give up: no event rather than a wrong one.
  var SETTLE_WAIT = 1000;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  // A product form carrying a live bundle-app offer is left to that app. It
  // binds its own capture-phase submit on the form and posts the chosen deal
  // with its own line properties; taking the add here would skip that. The
  // app's add is a normal cart request, so the drawer still refreshes and
  // opens on it (watchExternalChanges).
  var BUNDLE_MARKERS = '.rapi-bundles, .rapi-atc-btn, [name="properties[__rapi_offer]"], [name^="properties[_rapi_"]';

  function bundleOwnsForm(form) {
    if (form.querySelector(BUNDLE_MARKERS)) return true;
    var section = form.closest('.shopify-section');
    return !!(section && section.querySelector('.rapi-bundles'));
  }

  // The painting offer test's additions to the drawer (the cart-checked
  // refresh, the extra `side-cart:updated` events, the tab-return check) run
  // only while the offer is on. Liquid marks <html> with data-offer-count only
  // then (layout/theme.liquid). With the test off the drawer behaves as it
  // always has.
  function offerTestOn() {
    return document.documentElement.hasAttribute('data-offer-count');
  }

  function toJson(response) {
    return response.json().then(function (body) {
      if (!response.ok) {
        var error = new Error(body.description || body.message || 'Unable to update cart');
        error.cartError = body;
        throw error;
      }
      return body;
    });
  }

  // Carousel tiles that are on screen. A tile can be hidden by CSS: with the
  // painting offer test, the photo tile moves out of the carousel for a
  // visitor with an arm. A hidden tile takes no place and no arrow step.
  function shownSlides(wrapper) {
    return Array.prototype.filter.call(wrapper.querySelectorAll('.swiper-slide'), function (slide) {
      return window.getComputedStyle(slide).display !== 'none';
    });
  }

  // The drawer can hold more than one carousel: the offers, and with the
  // painting offer test "Complete your set" (data-carousel="set") above them.
  // Each keeps its own position across redraws, under this name.
  function carouselName(container) {
    return container.getAttribute('data-carousel') || 'offers';
  }

  class SideCartDrawer extends HTMLElement {
    constructor() {
      super();
      this.isOpen = false;
      this.isLoading = false;
      this.loadingTimer = null;
      this.savedBodyStyle = null;
      this.slides = {};
      this.slideOffsets = {};
      this.cart = null;
      this.ownRequests = [];
      this.refreshTimer = null;
      this.changingKeys = {};
      this.skipOpenUntil = 0;
      this.lastRefresh = 0;
      this.refreshSeq = 0;
      this.latestRefresh = null;
      this.quickAddOpen = null;
      this.onQuickAddKey = this.onQuickAddKey.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.layoutSlides = this.layoutSlides.bind(this);
    }

    connectedCallback() {
      if (this.mounted || document.querySelector(CART_APP_SCRIPT)) return;
      this.mounted = true;
      this.strictScrollLock = this.dataset.strictScrollLock === 'true';
      this.writeOfferCache();
      this.bindDrawer();
      this.bindSwipe();
      this.bindPage();
      this.watchExternalChanges();
      this.layoutSlides();
      this.openUploadFromLink();
      window.sideCart = {
        open: this.open.bind(this),
        close: this.close.bind(this),
        refresh: this.refresh.bind(this),
        get: this.get.bind(this),
        add: this.add.bind(this),
        update: this.update.bind(this),
        skipNextAutoOpen: this.skipNextAutoOpen.bind(this)
      };
    }

    // The custom photo builder shows its own screen after its add, so the
    // drawer must not also open on that add. Called just before the add.
    // The refresh and `side-cart:updated` still happen; only the open is
    // skipped. Expires if no add follows.
    skipNextAutoOpen() {
      this.skipOpenUntil = Date.now() + SKIP_OPEN_WAIT;
    }

    // True when this add's auto-open should be skipped. The first skipped add
    // shortens the window to a short tail, so a later add opens as normal.
    consumeSkipOpen() {
      var now = Date.now();
      if (now >= this.skipOpenUntil) return false;
      this.skipOpenUntil = Math.min(this.skipOpenUntil, now + SKIP_OPEN_TAIL);
      return true;
    }

    writeOfferCache() {
      // Two caches, two jobs. The offer list is the DISPOSABLE one cart
      // hygiene may clear a cart down to. The no-link list is every
      // configured offer, prefixed or not, and only stops a cart line from
      // linking out. Writing one list to both let a configured cross-sell
      // either be wiped or link out, depending which way it was filtered.
      this.writeHandleCache('cart-offer-handles-v1', this.dataset.offerHandles);
      this.writeHandleCache('cart-nolink-handles-v1', this.dataset.nolinkHandles);
    }

    writeHandleCache(key, raw) {
      var handles = [];
      try {
        handles = JSON.parse(raw || '[]');
      } catch (error) {}
      try {
        sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), handles: handles }));
      } catch (error) {}
    }

    emit(name, detail) {
      this.dispatchEvent(new CustomEvent('side-cart:' + name, { bubbles: true, detail: detail || {} }));
    }

    sectionsToRender() {
      return [
        { id: this.sectionId(), selector: '#side-cart', live: this },
        { id: 'header', selector: '.cart-link__icon', live: document.querySelector('#shopify-section-header .cart-link__icon') }
      ];
    }

    sectionId() {
      var wrapper = this.closest('.shopify-section');
      return wrapper ? wrapper.id.replace('shopify-section-', '') : 'side-cart';
    }

    sectionIds() {
      return this.sectionsToRender().map(function (section) { return section.id; });
    }

    getSectionInnerHTML(html, selector) {
      var parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return parsed ? parsed.innerHTML : null;
    }

    renderSections(sections) {
      if (!sections) return;
      var focus = this.focusedControl();
      // An open Quick Add popup outlives a redraw (the stamps and the gift
      // redraw right after any change): the live one, with the shopper's
      // choice, goes back in place of its new copy.
      var keep = this.quickAddOpen ? document.getElementById(this.quickAddOpen) : null;
      var keepFocus = keep && keep.contains(document.activeElement) ? document.activeElement : null;
      this.sectionsToRender().forEach(function (section) {
        var html = sections[section.id];
        if (!html || !section.live) return;
        var inner = this.getSectionInnerHTML(html, section.selector);
        if (inner !== null && section.live.innerHTML !== inner) section.live.innerHTML = inner;
      }, this);
      if (keep && !keep.isConnected) {
        var fresh = document.getElementById(this.quickAddOpen);
        if (fresh && this.contains(fresh)) fresh.replaceWith(keep);
      }
      this.applyState();
      if (keepFocus && keepFocus.isConnected) keepFocus.focus({ preventScroll: true });
      else this.restoreFocus(focus);
      this.afterRender();
    }

    // A re-render replaces the drawer's markup. Quick Add popups in it are new
    // elements: start the theme's Quick Add script on them. A popup whose
    // product left the suggestions (it was just added) is gone, so the drawer
    // lets go of its scroll lock.
    afterRender() {
      if (window.omdQuickBuy && typeof window.omdQuickBuy.init === 'function') window.omdQuickBuy.init(this);
      if (this.quickAddOpen && !this.querySelector('.u-custom-popup-overlay.active')) this.closeQuickAdd();
    }

    // The side cart's Quick Add popups (the offer test's suggested paintings)
    // open over the drawer. The drawer under them stops scrolling. Adding goes
    // through this drawer's own add (the page's capture-phase listeners), and
    // the re-render that follows closes the popup.
    openQuickAdd(trigger) {
      var id = trigger.getAttribute('data-quick-add-open');
      var popup = id && document.getElementById(id);
      if (!popup || !this.contains(popup)) return;
      this.closeQuickAdd(true);
      popup.classList.add('active');
      this.quickAddOpen = id;
      this.classList.add('side-cart--quick-add-open');
      // On the window, in the capture phase: the .com country selector
      // cancels every Escape at document level.
      window.addEventListener('keydown', this.onQuickAddKey, true);
      var close = popup.querySelector('.u-custom-popup-close');
      if (close) close.focus({ preventScroll: true });
    }

    closeQuickAdd(silent) {
      var id = this.quickAddOpen;
      this.quickAddOpen = null;
      window.removeEventListener('keydown', this.onQuickAddKey, true);
      this.classList.remove('side-cart--quick-add-open');
      Array.prototype.forEach.call(this.querySelectorAll('.u-custom-popup-overlay.active'), function (popup) {
        popup.classList.remove('active');
      });
      this.quickAddIdle();
      if (silent || !id) return;
      // Back to its Choose button. After an add that tile is gone: the
      // drawer's close button then keeps focus inside the drawer.
      var opener = Array.prototype.find.call(this.querySelectorAll('button[data-quick-add-open]'), function (button) {
        return button.getAttribute('data-quick-add-open') === id && button.tabIndex !== -1;
      }) || this.querySelector('.side-cart__close-button');
      if (opener) opener.focus({ preventScroll: true });
    }

    // An add from a popup is under way until the redraw: one add per tap,
    // even with Enter held down. A failed add hands the button back.
    quickAddBusy(popup, button) {
      popup.classList.add('is-adding');
      // After the click's own default action: a disabled submit button would
      // cancel the form submission some product-option apps rely on.
      setTimeout(function () { button.disabled = true; }, 0);
    }

    quickAddIdle() {
      Array.prototype.forEach.call(this.querySelectorAll('.u-custom-popup-overlay.is-adding'), function (popup) {
        popup.classList.remove('is-adding');
        var button = popup.querySelector('.u-buy-btn');
        if (button) button.disabled = false;
      });
    }

    // Keys while a popup is open: Escape closes it, Tab stays inside it.
    onQuickAddKey(event) {
      var popup = this.quickAddOpen && document.getElementById(this.quickAddOpen);
      if (!popup) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeQuickAdd();
        return;
      }
      if (event.key !== 'Tab') return;
      var items = Array.prototype.filter.call(popup.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'), function (item) {
        return !item.disabled && item.getClientRects().length > 0;
      });
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;
      if (!popup.contains(active) || (event.shiftKey && active === first) || (!event.shiftKey && active === last)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
      }
    }

    focusedControl() {
      var active = document.activeElement;
      if (!active || active === this || !this.contains(active) || !active.classList.length) return null;
      return { className: active.classList[0], key: active.dataset.key, code: active.dataset.code };
    }

    restoreFocus(focus) {
      if (!focus || this.contains(document.activeElement)) return;
      var match = Array.prototype.find.call(this.getElementsByClassName(focus.className), function (element) {
        return element.dataset.key === focus.key && element.dataset.code === focus.code;
      });
      if (match && !match.disabled) match.focus({ preventScroll: true });
    }

    applyState() {
      var overlay = this.querySelector('.side-cart__overlay');
      var panel = this.querySelector('.side-cart');
      var spinner = this.querySelector('.side-cart__loading-overlay');
      if (overlay) overlay.classList.toggle('is-open', this.isOpen);
      if (panel) {
        panel.classList.toggle('is-open', this.isOpen);
        panel.classList.toggle('loading', this.isOpen && this.isLoading);
      }
      if (spinner) spinner.classList.toggle('active', this.isLoading);
      this.querySelectorAll('.side-cart__discount-tag-button').forEach(function (button) {
        button.disabled = !!this.removingDiscount;
      }, this);
      this.layoutSlides();
    }

    startLoading() {
      clearTimeout(this.loadingTimer);
      this.isLoading = true;
      this.applyState();
      this.loadingTimer = setTimeout(this.stopLoading.bind(this), LOADING_TIMEOUT);
    }

    stopLoading() {
      clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
      this.isLoading = false;
      this.applyState();
    }

    // The drawer's own cart requests. A refresh that is under way when one
    // starts, or starts while it is in flight, may have read the cart from
    // before it: such a refresh is dropped (see refresh()).
    request(path, options) {
      var entry = { path: path, start: performance.now(), end: Infinity };
      this.ownRequests.push(entry);
      this.refreshSeq++;
      var finish = function () {
        entry.end = performance.now();
        this.refreshSeq++;
        setTimeout(function () {
          var index = this.ownRequests.indexOf(entry);
          if (index !== -1) this.ownRequests.splice(index, 1);
        }.bind(this), 10000);
      }.bind(this);
      return fetch(root() + path, options).then(function (response) {
        finish();
        return response;
      }, function (error) {
        finish();
        throw error;
      });
    }

    postJson(path, body) {
      body.sections = this.sectionIds();
      body.sections_url = window.location.pathname;
      return this.request(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body)
      }).then(toJson);
    }

    fetchCart() {
      return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          this.cart = cart;
          return cart;
        }.bind(this));
    }

    get() {
      return this.cart ? Promise.resolve(this.cart) : this.fetchCart();
    }

    // A plain read of the cart that leaves the cached cart alone.
    readCart() {
      return fetch(root() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) { return response.ok ? response.json() : null; })
        .catch(function () { return null; });
    }

    writing() {
      return this.ownRequests.some(function (request) { return request.end === Infinity; });
    }

    // True when the drawer shows this cart: the same total, and for every
    // line the same key, price and quantity (a locked gift line shows no
    // quantity). Prices matter: a discount changes them without changing keys.
    shows(cart) {
      var list = this.querySelector('.side-cart__items');
      var total = list ? Number(list.dataset.totalPrice) : 0;
      var drawn = {};
      Array.prototype.forEach.call(this.querySelectorAll('.side-cart__items > .side-cart__item[data-key]'), function (row) {
        var input = row.querySelector('.side-cart__quantity-input');
        drawn[row.dataset.key] = { quantity: input ? Number(input.value) : null, price: Number(row.dataset.finalLinePrice) };
      });
      var items = cart.items || [];
      if (total !== cart.total_price || items.length !== Object.keys(drawn).length) return false;
      return items.every(function (item) {
        var line = Object.prototype.hasOwnProperty.call(drawn, item.key) ? drawn[item.key] : null;
        return !!line && line.price === item.final_line_price && (line.quantity === null || line.quantity === item.quantity);
      });
    }

    refreshAfterWrites() {
      var waited = 0;
      var check = function () {
        if (this.writing() && (waited += 100) < 10000) return setTimeout(check, 100);
        this.refresh();
      }.bind(this);
      check();
    }

    // While the painting offer is on, every re-render ends in `side-cart:updated`,
    // whatever caused it: an add from another script, a stamping pass, a
    // return to the tab.
    // The event only ever carries the cart the drawer shows. The cart is read
    // after the markup is drawn; if it has moved on since (another script
    // wrote in between), the drawer is drawn again, up to twice, then once
    // more after a short wait. A cart that still does not match is never
    // sent: the write that moved it brings its own refresh. If the cart
    // cannot be read, nothing is sent and the drawer is drawn once more
    // after the same wait.
    // Only the newest refresh renders and emits: an older one that answers
    // late is dropped and resolves at once. The drawer on screen is already
    // the newest one drawn, so `refresh().then(open)` opens without waiting,
    // and the newer refresh updates it in place. Dropped for one of the
    // drawer's own writes with no newer refresh to come, it runs again once
    // those writes are done, so a change from elsewhere is not lost. The own
    // write sends its own event.
    refresh() {
      if (!offerTestOn()) return this.plainRefresh();
      return this.draw(0, false);
    }

    // The refresh as it has always been: draw the sections that come back, no
    // event. Used while the offer test is off.
    plainRefresh() {
      var url = window.location.pathname + '?sections=' + this.sectionIds().join(',');
      return fetch(url)
        .then(function (response) { return response.json(); })
        .then(function (sections) {
          this.renderSections(sections);
          this.cart = null;
        }.bind(this))
        .catch(function () {});
    }

    draw(attempt, settling) {
      this.lastRefresh = Date.now();
      var seq = ++this.refreshSeq;
      var url = window.location.pathname + '?sections=' + this.sectionIds().join(',');
      var run = fetch(url)
        .then(function (response) { return response.json(); })
        .then(function (sections) {
          if (seq !== this.refreshSeq) {
            if (this.latestRefresh === run) this.refreshAfterWrites();
            return null;
          }
          this.renderSections(sections);
          this.cart = null;
          return this.readCart().then(function (current) {
            if (seq !== this.refreshSeq) return;
            var later = function () {
              if (settling) return;
              setTimeout(function () {
                if (seq === this.refreshSeq) this.draw(0, true);
              }.bind(this), SETTLE_WAIT);
            }.bind(this);
            // The cart could not be read: there is nothing to check the
            // drawer against, so send nothing and try once more later.
            if (!current) return later();
            if (!this.shows(current)) {
              if (attempt < REDRAW_LIMIT) this.draw(attempt + 1, settling);
              else later();
              return;
            }
            this.emit('updated', { cart: current });
          }.bind(this));
        }.bind(this))
        .catch(function () {});
      this.latestRefresh = run;
      return run;
    }

    add(payload) {
      if (!this.isOpen) this.open();
      this.startLoading();
      var options;
      var variantId;
      var quantity;
      var sections = this.sectionIds();
      if (payload instanceof FormData) {
        variantId = payload.get('id');
        quantity = Number(payload.get('quantity') || 1);
        if (payload.get('ids[]')) {
          payload = {
            items: payload.getAll('ids[]').map(function (id) { return { id: id, quantity: 1 }; })
          };
        } else {
          payload.append('sections', sections.join(','));
          payload.append('sections_url', window.location.pathname);
          options = { method: 'POST', headers: { Accept: 'application/json' }, body: payload };
        }
      } else {
        variantId = payload.id;
        quantity = payload.quantity || 1;
      }
      if (!options) {
        payload = Object.assign({}, payload, { sections: sections, sections_url: window.location.pathname });
        options = {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        };
      }
      var added = false;
      return this.request('cart/add.js', options)
        .then(toJson)
        .then(function (result) {
          added = true;
          if (!result || !result.sections) throw new Error('Added, but the drawer was not re-rendered');
          this.renderSections(result.sections);
          return this.fetchCart();
        }.bind(this))
        .then(function (cart) {
          this.stopLoading();
          this.emit('item-added', { variantId: variantId, quantity: quantity, cart: cart });
          this.emit('updated', { cart: cart });
          return cart;
        }.bind(this))
        .catch(function (error) {
          // Redraw from the real cart either way. Only an add the store
          // refused is reported, with the store's own reason (sold out,
          // quantity limit), as a failed quantity change is. An add that went
          // through but could not be redrawn is not a failure to the shopper.
          this.quickAddIdle();
          this.stopLoading();
          this.refresh();
          if (added) return;
          alert(
            (error && error.cartError && (error.cartError.description || error.cartError.message)) ||
            'Unable to add to cart right now. Please try again.'
          );
        }.bind(this));
    }

    update(body) {
      return this.postJson('cart/update.js', Object.assign({}, body))
        .then(function (cart) {
          this.cart = cart;
          this.renderSections(cart.sections);
          delete cart.sections;
          if (offerTestOn()) this.emit('updated', { cart: cart });
          return cart;
        }.bind(this))
        .catch(function () {});
    }

    // A line is targeted by its key, never its position. Other scripts (the
    // gift app) add lines between a render and a tap, which shifts every
    // position, so a position could change or remove the wrong line.
    // One change per line at a time: the overlay stops the mouse but not
    // the keyboard, so a second Enter would otherwise send a second request.
    changeLine(key, quantity) {
      if (!key || this.changingKeys[key]) return Promise.resolve();
      this.changingKeys[key] = true;
      var row = this.lineRow(key);
      var variantId = row ? Number(row.dataset.variantId) : null;
      var input = quantity > 0 && row && row.querySelector('.side-cart__quantity-input');
      if (input) input.value = quantity;
      this.startLoading();
      return this.postJson('cart/change.js', { id: key, quantity: quantity })
        .then(function (cart) {
          delete this.changingKeys[key];
          this.cart = cart;
          this.renderSections(cart.sections);
          delete cart.sections;
          if (quantity === 0) {
            this.emit('item-removed', { key: key, variantId: variantId, cart: cart });
          } else {
            var item = cart.items.find(function (entry) { return entry.key === key; });
            this.emit('quantity-changed', { key: key, variantId: item ? item.variant_id : variantId, quantity: quantity, cart: cart });
          }
          this.emit('updated', { cart: cart });
          this.stopLoading();
          return cart;
        }.bind(this))
        .catch(function (error) {
          delete this.changingKeys[key];
          this.stopLoading();
          this.refresh();
          alert(
            (error && error.cartError && (error.cartError.description || error.cartError.message)) ||
            (error && error.message) ||
            'Unable to update cart right now. Please try again.'
          );
        }.bind(this));
    }

    lineRow(key) {
      return Array.prototype.find.call(this.querySelectorAll('.side-cart__items > .side-cart__item'), function (row) {
        return row.dataset.key === key;
      }) || null;
    }

    addOffer(button) {
      this.startLoading();
      var variantId = Number(button.dataset.variantId);
      var title = button.dataset.title;
      return this.request('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: variantId,
          quantity: Number(button.dataset.quantity) || 1,
          sections: this.sectionIds(),
          sections_url: window.location.pathname
        })
      })
        .then(toJson)
        .then(function (result) {
          this.renderSections(result.sections);
          return this.fetchCart();
        }.bind(this))
        .then(function (cart) {
          this.emit('offer-added', { variantId: variantId, cart: cart });
          this.emit('updated', { cart: cart });
          this.stopLoading();
        }.bind(this))
        .catch(function () {
          this.stopLoading();
          alert('We couldn\'t add "' + (title || 'this item') + '" to your cart right now. It may be out of stock or temporarily unavailable. Please try again later.');
        }.bind(this));
    }

    // Swaps a line to its upgrade variant. The new variant goes in first,
    // with the line's properties (the customer's photo and crop) copied
    // across, so a failure never leaves the cart without the item.
    upgradeLine(button) {
      var key = button.dataset.key;
      if (!key || this.changingKeys[key]) return Promise.resolve();
      this.startLoading();
      var variantId = Number(button.dataset.variantId);
      var quantity = Number(button.dataset.quantity) || 1;
      var value = button.dataset.optionValue;
      // Liquid serialises line properties as [name, value] pairs.
      var properties = {};
      try {
        var parsed = JSON.parse(button.dataset.properties || '{}') || {};
        if (Array.isArray(parsed)) {
          parsed.forEach(function (pair) {
            if (Array.isArray(pair) && pair.length === 2) properties[pair[0]] = pair[1];
          });
        } else {
          properties = parsed;
        }
      } catch (error) {
        // Never swap without the line's properties: that would drop the photo.
        this.stopLoading();
        return Promise.resolve();
      }
      this.changingKeys[key] = true;
      var added = null;
      var removed = false;
      FRAME_PROPERTIES.forEach(function (name) {
        if (Object.prototype.hasOwnProperty.call(properties, name)) properties[name] = value;
      });
      return this.request('cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity, properties: properties })
      })
        .then(toJson)
        .then(function (item) {
          added = item;
          return this.postJson('cart/change.js', { id: key, quantity: 0 });
        }.bind(this))
        .then(function (cart) {
          removed = true;
          delete this.changingKeys[key];
          this.cart = cart;
          this.renderSections(cart.sections);
          delete cart.sections;
          this.emit('item-upgraded', { variantId: variantId, quantity: quantity, cart: cart });
          this.emit('updated', { cart: cart });
          this.stopLoading();
        }.bind(this))
        .catch(function (error) {
          // The new line went in but the old one would not come out. Take
          // the new one back out, so the shopper is never left with two.
          // Only when the cart still holds the old line: a dropped response
          // can hide a remove that went through, and rolling back then
          // would leave no painting at all. Only the quantity this swap
          // added: the add may have merged into a line already there.
          var rollback = added && added.key && !removed
            ? this.fetchCart().then(function (cart) {
              var stillThere = cart.items.some(function (item) { return item.key === key; });
              if (!stillThere) return;
              return this.postJson('cart/change.js', { id: added.key, quantity: Math.max(0, added.quantity - quantity) });
            }.bind(this)).catch(function () {})
            : Promise.resolve();
          return rollback.then(function () {
            delete this.changingKeys[key];
            this.stopLoading();
            this.refresh();
            alert(
              (error && error.cartError && (error.cartError.description || error.cartError.message)) ||
              'Unable to update cart right now. Please try again.'
            );
          }.bind(this));
        }.bind(this));
    }

    // Arriving from the photo offer: open the photo app's upload window
    // once its button renders, then drop the parameter so a reload does
    // not open it again.
    openUploadFromLink() {
      var url;
      try { url = new URL(window.location.href); } catch (error) { return; }
      if (url.searchParams.get(UPLOAD_PARAM) !== '1') return;
      url.searchParams.delete(UPLOAD_PARAM);
      try { history.replaceState(history.state, '', url.pathname + url.search + url.hash); } catch (error) {}
      var started = Date.now();
      var tryOpen = function () {
        var button = document.querySelector(UPLOAD_BUTTON);
        if (button) {
          button.click();
          return;
        }
        if (Date.now() - started < UPLOAD_WAIT) setTimeout(tryOpen, 200);
      };
      tryOpen();
    }

    removeDiscount(code) {
      this.startLoading();
      this.removingDiscount = true;
      this.applyState();
      var remaining = Array.prototype.map.call(this.querySelectorAll('.side-cart__discount-tag-button[data-code]'), function (button) {
        return button.dataset.code;
      }).filter(function (other, index, all) {
        return other.toLowerCase() !== code.toLowerCase() && all.indexOf(other) === index;
      });
      return this.postJson('cart/update.js', { discount: remaining.join(',') })
        .then(function (cart) {
          this.cart = cart;
          this.removingDiscount = false;
          this.renderSections(cart.sections);
          delete cart.sections;
          if (window.EasyGift && window.EasyGift.RefreshCart) window.EasyGift.RefreshCart(cart);
          if (offerTestOn()) this.emit('updated', { cart: cart });
          this.stopLoading();
        }.bind(this))
        .catch(function () {
          this.removingDiscount = false;
          this.stopLoading();
        }.bind(this));
    }

    open() {
      if (this.isOpen) return;
      this.isOpen = true;
      this.lockScroll();
      this.applyState();
    }

    close() {
      if (!this.isOpen) return;
      if (this.quickAddOpen) this.closeQuickAdd(true);
      this.isOpen = false;
      this.unlockScroll();
      this.applyState();
    }

    lockScroll() {
      var body = document.body;
      if (this.strictScrollLock) {
        this.savedBodyStyle = {};
        LOCK_PROPS.forEach(function (prop) {
          this.savedBodyStyle[prop] = { value: body.style.getPropertyValue(prop), priority: body.style.getPropertyPriority(prop) };
        }, this);
        body.style.setProperty('top', '-' + window.pageYOffset + 'px', 'important');
        body.style.setProperty('position', 'fixed', 'important');
        body.style.setProperty('overflow', 'hidden', 'important');
        body.style.setProperty('width', '100%', 'important');
      } else {
        body.style.top = '-' + window.pageYOffset + 'px';
      }
      body.classList.add('side-cart-is-open');
      if (window.getComputedStyle(body).position === 'fixed') window.scrollTo(0, 0);
      document.addEventListener('focusout', this.onFocusOut);
    }

    unlockScroll() {
      var body = document.body;
      var top = body.style.top.split('.')[0].replace(/\D/g, '');
      body.classList.remove('side-cart-is-open');
      if (this.strictScrollLock) {
        var saved = this.savedBodyStyle;
        LOCK_PROPS.forEach(function (prop) {
          var entry = saved && saved[prop];
          if (entry && entry.value) body.style.setProperty(prop, entry.value, entry.priority);
          else body.style.removeProperty(prop);
        });
        this.savedBodyStyle = null;
      } else {
        body.style.removeProperty('top');
      }
      if (top.length > 1) window.scrollTo(0, top);
      document.removeEventListener('focusout', this.onFocusOut);
    }

    onFocusOut(event) {
      if (!(event.target && event.target.closest && event.target.closest('.side-cart'))) return;
      setTimeout(function () {
        var active = document.activeElement;
        if (active && active.closest && active.closest('.side-cart') && /^(INPUT|SELECT|TEXTAREA)$/.test(active.tagName)) return;
        if (window.getComputedStyle(document.body).position === 'fixed') window.scrollTo(0, 0);
      }, 50);
    }

    // Lays out every carousel in the drawer. `animate` (true) slides
    // `only`, or every carousel when `only` is left out. A resize or a
    // re-render snaps each one into place.
    layoutSlides(animate, only) {
      Array.prototype.forEach.call(this.querySelectorAll('.side-cart__upsells .swiper'), function (container) {
        this.layoutCarousel(container, animate === true && (!only || only === container));
      }, this);
    }

    layoutCarousel(container, animate) {
      var wrapper = container.querySelector('.swiper-wrapper');
      if (!wrapper) return;
      var slides = shownSlides(wrapper);
      if (!slides.length) return;
      var name = carouselName(container);
      var count = slides.length;
      var active = (((this.slides[name] || 0) % count) + count) % count;
      var peeking = count > 1;
      // Set the peek class before measuring: it changes the container's width.
      var block = container.closest('.side-cart__upsells');
      if (block) block.classList.toggle('side-cart__upsells--peek', peeking);
      var width = container.clientWidth;
      if (!width) return;
      var gap = peeking ? PEEK_GAP : SLIDE_GAP;
      var slideWidth = peeking ? Math.max(width - PEEK_RESERVE, Math.round(width * PEEK_MIN_RATIO)) : width;
      var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      // Only an arrow press or a swipe slides.
      wrapper.style.transitionDuration = animate && !reduceMotion ? SLIDE_DURATION + 'ms' : '0ms';
      // Clear the old height first, or stretched slides would measure it back.
      wrapper.style.height = '';
      var tallest = 0;
      slides.forEach(function (slide, index) {
        slide.style.width = slideWidth + 'px';
        slide.style.marginRight = gap + 'px';
        slide.classList.toggle('swiper-slide-active', index === active);
        slide.classList.toggle('swiper-slide-next', count > 1 && index !== active && index === (active + 1) % count);
        slide.classList.toggle('swiper-slide-prev', count > 1 && index !== active && index !== (active + 1) % count && index === (active - 1 + count) % count);
        if (slide.offsetHeight > tallest) tallest = slide.offsetHeight;
      });
      wrapper.style.height = (peeking ? tallest : slides[active].offsetHeight) + 'px';
      this.slideOffsets[name] = -(active * (slideWidth + gap));
      wrapper.style.transform = 'translate3d(' + this.slideOffsets[name] + 'px, 0px, 0px)';
    }

    // True for the click that ends a swipe on a carousel. The drawer swallows
    // it; a page-wide listener that runs first can ask, so it is not counted
    // as a tap (assets/offer-test-cart.js).
    swipeClick() {
      return !!this.swipedAt && Date.now() - this.swipedAt < SWIPE_CLICK_WINDOW;
    }

    // One step along a carousel: an arrow press or a swipe.
    moveCarousel(container, step) {
      var name = carouselName(container);
      this.slides[name] = (this.slides[name] || 0) + step;
      this.layoutSlides(true, container);
    }

    // A control reached with the keyboard in a tile that is off screen brings
    // its tile into view. The browser would otherwise scroll the clipped
    // carousel by itself and leave it out of step with its arrows.
    showFocusedSlide(control) {
      var slide = control.closest && control.closest('.side-cart__upsells .swiper-slide');
      if (!slide) return;
      var container = slide.closest('.swiper');
      container.scrollLeft = 0;
      var slides = shownSlides(container.querySelector('.swiper-wrapper'));
      var index = slides.indexOf(slide);
      if (index < 0) return;
      var name = carouselName(container);
      var count = slides.length;
      if ((((this.slides[name] || 0) % count) + count) % count === index) return;
      this.slides[name] = index;
      this.layoutSlides(true, container);
    }

    /*
     * Touch and mouse swipe on the offer carousel. The tiles follow the finger,
     * then a long enough drag moves one offer, the same step as an arrow press.
     * A vertical drag is left to the page, so the drawer still scrolls. The CSS
     * sets touch-action: pan-y on the carousel so the browser hands over the
     * horizontal moves. A swipe that ends on a tile must not also press it.
     */
    bindSwipe() {
      var drag = null;

      var end = function (event) {
        if (!drag || event.pointerId !== drag.id) return;
        var swiped = drag.axis === 'x';
        var dx = drag.dx;
        var container = drag.container;
        drag = null;
        if (!swiped) return;
        this.swipedAt = Date.now();
        if (Math.abs(dx) >= SWIPE_THRESHOLD) this.moveCarousel(container, dx < 0 ? 1 : -1);
        else this.layoutSlides(true, container);
      }.bind(this);

      this.addEventListener('pointerdown', function (event) {
        // A touch swipe fires no click, so a guard left from it must not eat the next tap.
        this.swipedAt = 0;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        var target = event.target;
        if (!target.closest || target.closest('.swiper-button-next, .swiper-button-prev')) return;
        var container = target.closest('.side-cart__upsells .swiper');
        if (!container || shownSlides(container.querySelector('.swiper-wrapper')).length < 2) return;
        drag = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
          dx: 0,
          axis: null,
          container: container,
          wrapper: container.querySelector('.swiper-wrapper'),
          start: this.slideOffsets[carouselName(container)] || 0
        };
      }.bind(this));

      this.addEventListener('pointermove', function (event) {
        if (!drag || event.pointerId !== drag.id) return;
        // A mouse released outside the window sends no pointerup. Settle the tiles.
        if (event.pointerType === 'mouse' && event.buttons === 0) {
          end(event);
          return;
        }
        var dx = event.clientX - drag.x;
        var dy = event.clientY - drag.y;
        if (!drag.axis) {
          if (Math.abs(dx) < SWIPE_SLOP && Math.abs(dy) < SWIPE_SLOP) return;
          drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
          if (drag.axis === 'y') {
            drag = null;
            return;
          }
        }
        drag.dx = dx;
        drag.wrapper.style.transitionDuration = '0ms';
        drag.wrapper.style.transform = 'translate3d(' + (drag.start + dx) + 'px, 0px, 0px)';
      });

      // A mouse drag on a tile image would start the browser's own image drag.
      this.addEventListener('dragstart', function (event) {
        if (event.target.closest && event.target.closest('.side-cart__upsells .swiper')) event.preventDefault();
      });

      this.addEventListener('pointerup', end);
      this.addEventListener('pointercancel', end);

      // Capture phase, so the swipe's own click never reaches a tile's link or add button.
      this.addEventListener('click', function (event) {
        if (this.swipeClick()) {
          this.swipedAt = 0;
          event.preventDefault();
          event.stopPropagation();
        }
      }.bind(this), true);
    }

    bindDrawer() {
      window.addEventListener('resize', this.layoutSlides);
      this.addEventListener('load', this.layoutSlides, true);

      // Keyboard focus only: a tap must not slide the tiles under the finger.
      // With the painting offer test off the drawer behaves as before.
      this.addEventListener('focusin', function (event) {
        if (!offerTestOn()) return;
        var control = event.target;
        var keyboard = false;
        try { keyboard = control.matches(':focus-visible'); } catch (error) {}
        if (keyboard) this.showFocusedSlide(control);
      }.bind(this));

      this.addEventListener('click', function (event) {
        var target = event.target;
        if (!target.closest) return;

        if (target.classList.contains('side-cart__overlay') || target.closest('.side-cart__close-button')) {
          this.close();
          return;
        }

        var quickAdd = target.closest('[data-quick-add-open]');
        if (quickAdd) {
          this.openQuickAdd(quickAdd);
          return;
        }

        // Inside an open Quick Add popup: a tap on the backdrop or the close
        // button closes it. The page-wide popup script must not also run: it
        // would clear the scroll lock the open drawer holds on the page.
        var popup = target.closest('.u-custom-popup-overlay');
        if (popup) {
          if (target === popup || target.closest('.u-custom-popup-close')) {
            event.stopPropagation();
            this.closeQuickAdd();
          } else {
            var buy = target.closest('.u-buy-btn');
            if (buy && !buy.disabled && popup.classList.contains('active')) this.quickAddBusy(popup, buy);
          }
          return;
        }

        var remove = target.closest('.side-cart__remove-button');
        if (remove) {
          if (!remove.disabled) this.changeLine(remove.dataset.key, 0);
          return;
        }

        var step = target.closest('.side-cart__quantity-minus, .side-cart__quantity-plus');
        if (step) {
          if (!step.disabled) this.changeLine(step.dataset.key, Number(step.dataset.quantity));
          return;
        }

        var upgrade = target.closest('.side-cart__item-upgrade');
        if (upgrade) {
          if (!upgrade.disabled) this.upgradeLine(upgrade);
          return;
        }

        var upload = target.closest('.side-cart__upsell-item-upload');
        if (upload) {
          window.location.href = upload.dataset.url;
          return;
        }

        var booklet = target.closest('[data-cart-booklet-input]');
        if (booklet) {
          // Side cart offers: the tick box adds 3 booklets with the marker,
          // and unticking removes that line. The click has already flipped
          // the box, so it is put back until the drawer has been redrawn.
          var ticked = booklet.checked;
          booklet.checked = !ticked;
          if (ticked) {
            this.add({
              id: Number(booklet.dataset.variantId),
              quantity: Number(booklet.dataset.quantity) || 3,
              properties: { _offer_source: 'side_cart' }
            });
          } else if (booklet.dataset.lineKey) {
            this.changeLine(booklet.dataset.lineKey, 0);
          }
          return;
        }

        var offer = target.closest('.side-cart__upsell-item-add');
        if (offer) {
          this.addOffer(offer);
          return;
        }

        var arrow = target.closest('.swiper-button-next, .swiper-button-prev');
        if (arrow) {
          var track = arrow.closest('.swiper');
          if (track) this.moveCarousel(track, arrow.classList.contains('swiper-button-next') ? 1 : -1);
          return;
        }

        var discount = target.closest('.side-cart__discount-tag-button');
        if (discount) {
          if (!discount.disabled) this.removeDiscount(discount.dataset.code);
          return;
        }

        if (target.closest('.side-cart__checkout-button')) {
          window.location.href = this.dataset.checkoutUrl;
        }
      }.bind(this));

      // A typed quantity applies on Enter or blur, snapped down onto the
      // variant's quantity rules. 0 removes the line; anything else invalid
      // puts the current quantity back.
      this.addEventListener('keydown', function (event) {
        if (event.key === 'Enter' && event.target.matches && event.target.matches('.side-cart__quantity-input')) {
          event.preventDefault();
          event.target.blur();
        }
      });

      this.addEventListener('change', function (event) {
        var input = event.target;
        if (!input.matches || !input.matches('.side-cart__quantity-input') || input.disabled) return;
        var key = input.dataset.key;
        var current = Number(input.defaultValue);
        var typed = input.value.trim();
        var quantity = /^\d+$/.test(typed) ? Number(typed) : NaN;
        if (quantity > 0) {
          var min = Number(input.dataset.min) || 1;
          var increment = Number(input.dataset.increment) || 1;
          var max = input.dataset.max ? Number(input.dataset.max) : Infinity;
          if (quantity > max) quantity = max;
          quantity = quantity < min ? min : min + Math.floor((quantity - min) / increment) * increment;
        }
        if (isNaN(quantity) || quantity === current || this.changingKeys[key]) {
          input.value = current;
          return;
        }
        this.changeLine(key, quantity);
      }.bind(this));
    }

    bindPage() {
      var drawer = this;
      var body = document.body;

      var shoppad = window.Shoppad = window.Shoppad || {};
      shoppad.apps = shoppad.apps || {};
      shoppad.apps.infiniteoptions = shoppad.apps.infiniteoptions || {};
      shoppad.apps.infiniteoptions.beforeReady = function (register) {
        register('productBundleCartSubmit', function (event) {
          event.preventDefault();
          event.stopPropagation();
          setTimeout(function () {
            drawer.add(new FormData(document.querySelector('form[action*="/cart/add"]')));
          }, 500);
        });
      };

      // The theme's own cart drawer only renders when the Cart type setting is
      // "Drawer". This store is on "Page", so it is absent. If someone switches
      // it, swallow its open event so two drawers can never show at once. This
      // event belongs to that drawer alone, so nothing else loses a listener.
      document.addEventListener('theme:open-cart-drawer', function (event) {
        event.stopImmediatePropagation();
        drawer.refresh().then(function () { drawer.open(); });
      }, true);

      body.addEventListener('submit', function (event) {
        if (event.defaultPrevented || !event.target.getAttribute('method')) return;
        var action = event.target.getAttribute('action');
        if (action && action.indexOf('/cart/add') !== -1) {
          if (bundleOwnsForm(event.target)) return;
          event.preventDefault();
          event.stopPropagation();
          if (!drawer.productFormValid(event.target)) return;
          drawer.closePageQuickAdd(event.target);
          drawer.add(new FormData(event.target));
        }
      }, true);

      body.addEventListener('click', async function (event) {
        var target = event.target;
        if (!target || !target.closest) return;

        if (target.closest('[data-side-cart-open]') || target.closest('a[href$="/cart"]')) {
          event.preventDefault();
          event.stopPropagation();
          drawer.refresh().then(function () { drawer.open(); });
        }

        var submit = target.closest('button[type="submit"]:not([disabled])') ||
          target.closest('input[type="submit"][name="add"]:not([disabled])') ||
          target.closest('[data-side-cart-add]');
        var quickBuy = target.closest('button[data-quick-buy][data-variant-id]');

        if (shoppad.apps.infiniteoptions._fieldsReady) return;
        if (submit && submit.classList.contains('gf_button-soldout')) return;
        if (submit && target.closest('form.kaching-bundles-form--different-variants-selected')) return;

        var form = submit && target.closest('form[action*="/cart/add"]');
        if (form && bundleOwnsForm(form)) form = null;
        if (form) {
          // preventDefault alone stops the add: the theme's own ajax add is
          // bound to the form's submit event, which a prevented click never
          // reaches. stopPropagation here would ALSO stop the button's own
          // listeners, and the GTM dataLayer add_to_cart push is one of them.
          event.preventDefault();
          if (!drawer.productFormValid(form)) return;
          if (drawer.hasCheckedPageAddon()) {
            try { await drawer.waitForAddonAdded(5000); } catch (error) {}
          }
          drawer.closePageQuickAdd(form);
          drawer.add(new FormData(form));
        }

        if (quickBuy) {
          event.preventDefault();
          event.stopPropagation();
          drawer.add({ id: quickBuy.getAttribute('data-variant-id') || quickBuy.getAttribute('data-variantid'), quantity: 1 });
        }
      }, true);
    }

    // A Quick Add popup on a collection card is not part of the drawer, but the
    // drawer's capture-phase listeners take its add. The popup's own submit
    // handler (which closes it and releases the page scroll lock) never runs.
    // Close it here, before the drawer opens: the drawer saves the page's
    // scroll lock state when it opens, and would restore the popup's lock.
    closePageQuickAdd(form) {
      var popup = form && form.closest && form.closest('.u-custom-popup-overlay:not([data-quick-add-scope])');
      if (!popup || !popup.classList.contains('active')) return;
      popup.classList.remove('active');
      if (!this.isOpen) document.body.style.overflow = '';
    }

    // The theme validates its product form in a submit handler that this
    // drawer's capture-phase listeners pre-empt. Run the same check here, so
    // an unchosen option is flagged and nothing is added. A form the theme
    // has not set up is left to the add itself, as before.
    productFormValid(form) {
      var container = form.closest('.product-form');
      if (!container || !window.jQuery || !window.theme || typeof theme.validateProductForm !== 'function') return true;
      try {
        return theme.validateProductForm(window.jQuery(container)) !== false;
      } catch (error) {
        return true;
      }
    }

    hasCheckedPageAddon() {
      return !!document.body.querySelector('lb-upsell-product-page-addon input.lb-spc-details-checkbox:checked');
    }

    waitForAddonAdded(timeout) {
      return new Promise(function (resolve, reject) {
        var timer;
        function done(event) {
          clearTimeout(timer);
          window.removeEventListener('lb-upsell-added', done);
          resolve(event);
        }
        window.addEventListener('lb-upsell-added', done);
        timer = setTimeout(function () {
          window.removeEventListener('lb-upsell-added', done);
          reject();
        }, timeout);
      });
    }

    watchExternalChanges() {
      var drawer = this;
      var openAfterRefresh = false;
      var scheduleRefresh = function (open) {
        if (open) openAfterRefresh = true;
        clearTimeout(drawer.refreshTimer);
        drawer.refreshTimer = setTimeout(function () {
          var shouldOpen = openAfterRefresh;
          openAfterRefresh = false;
          drawer.refresh().then(function () {
            if (shouldOpen) drawer.open();
          });
        }, 100);
      };

      // The offer test's stamping engine re-checks the cart after any cart
      // change. Its own requests carry omd_stamp, so they never loop back.
      var scheduleStamps = function () {
        if (window.omdOfferStamps) window.omdOfferStamps.schedule();
      };

      document.addEventListener('theme:cartchanged', function (event) {
        scheduleStamps();
        var open = !!(event.detail && event.detail.openDrawer);
        scheduleRefresh(open && !drawer.consumeSkipOpen());
      });
      window.addEventListener('pageshow', function (event) {
        if (event.persisted) scheduleRefresh(false);
      });

      // Back on this tab while the painting offer test runs: the cart may have
      // changed in another one, and the offer surfaces must follow it. Read
      // it and redraw only if it differs from the drawer on screen; otherwise
      // just announce it, so a drawer left open keeps its scroll position.
      // With the test off nothing happens here, as before.
      var refreshOnReturn = function () {
        if (!offerTestOn()) return;
        if (document.visibilityState === 'hidden') return;
        if (Date.now() - drawer.lastRefresh < FOCUS_REFRESH_GAP || drawer.writing()) return;
        drawer.lastRefresh = Date.now();
        drawer.readCart().then(function (cart) {
          if (!cart || drawer.writing()) return;
          if (drawer.shows(cart)) drawer.emit('updated', { cart: cart });
          else scheduleRefresh(false);
        });
      };
      window.addEventListener('focus', refreshOnReturn);
      document.addEventListener('visibilitychange', refreshOnReturn);

      if (!('PerformanceObserver' in window)) return;
      try {
        new PerformanceObserver(function (list) {
          list.getEntries().forEach(function (entry) {
            if (entry.initiatorType !== 'fetch' && entry.initiatorType !== 'xmlhttprequest') return;
            var url;
            try { url = new URL(entry.name); } catch (error) { return; }
            var path = url.pathname;
            if (!CART_PATHS.test(path)) return;
            if (url.searchParams.has('omd_stamp')) return;
            scheduleStamps();
            var own = drawer.ownRequests.some(function (request) {
              return path.slice(-request.path.length) === request.path &&
                entry.startTime >= request.start - 5 && entry.startTime <= request.end + 5;
            });
            if (own) return;
            var open = ADD_PATH.test(path) && !url.searchParams.has(GIFT_APP_PARAM) && !url.searchParams.has('omd_gift');
            scheduleRefresh(open && !drawer.consumeSkipOpen());
          });
        }).observe({ type: 'resource', buffered: false });
      } catch (error) {}
    }
  }

  function define() {
    if (!customElements.get('side-cart-drawer')) customElements.define('side-cart-drawer', SideCartDrawer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', define, { once: true });
  } else {
    define();
  }
})();
